import { useLayoutEffect, useRef, useState } from "react";

const IMAGE_PLACEHOLDER_SRC = "/images/placeholder_image.png";
const TARGET_TABLE_WIDTH_PX = 1245;
const MIN_REPORT_PREVIEW_HEIGHT_PX = 520;

function getSafeImageSrc(src) {
  return typeof src === "string" && src.trim() ? src : IMAGE_PLACEHOLDER_SRC;
}

export default function Ud1000VideoSummaryTable({
  tableRef,
  t,
  draftData,
  disableResponsiveScale = false,
  maxViewportHeight = null,
  imgTemp,
  previewScale = 1,
  toFusionLabel,
  ultrasonicRef1,
  ultrasonicRef2,
  fftRef1,
  fftRef2,
}) {
  const tableWrapRef = useRef(null);
  const tableStageRef = useRef(null);
  const [responsiveLayout, setResponsiveLayout] = useState({
    scale: 1,
    height: null,
  });
  const numericPreviewScale = Number(previewScale);
  const previewScaleMultiplier =
    Number.isFinite(numericPreviewScale) && numericPreviewScale > 0
      ? numericPreviewScale
      : 1;

  useLayoutEffect(() => {
    if (disableResponsiveScale) {
      setResponsiveLayout({
        scale: 1,
        height: null,
      });
      return undefined;
    }

    const wrapElement = tableWrapRef.current;
    const stageElement = tableStageRef.current;

    if (!wrapElement || !stageElement) return undefined;

    let frameId = 0;

    const measure = () => {
      const widthScale = wrapElement.clientWidth / TARGET_TABLE_WIDTH_PX;
      const naturalHeight = stageElement.offsetHeight || 0;
      const heightScale =
        maxViewportHeight != null && maxViewportHeight > 0 && naturalHeight > 0
          ? maxViewportHeight / naturalHeight
          : 1;
      const nextScale =
        Math.min(1, widthScale, heightScale) * previewScaleMultiplier;
      const nextHeight = Number((naturalHeight * nextScale).toFixed(2));

      setResponsiveLayout((prev) => {
        const prevHeight = prev.height ?? 0;

        if (
          Math.abs(prev.scale - nextScale) < 0.001 &&
          Math.abs(prevHeight - nextHeight) < 0.5
        ) {
          return prev;
        }

        return {
          scale: nextScale,
          height: nextHeight,
        };
      });
    };

    const scheduleMeasure = () => {
      cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(measure);
    };

    scheduleMeasure();

    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(() => {
            scheduleMeasure();
          });

    resizeObserver?.observe(wrapElement);
    resizeObserver?.observe(stageElement);
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [disableResponsiveScale, maxViewportHeight, previewScaleMultiplier]);

  const minPreviewHeight = disableResponsiveScale
    ? 0
    : Math.max(
        0,
        Math.min(
          maxViewportHeight ?? MIN_REPORT_PREVIEW_HEIGHT_PX,
          MIN_REPORT_PREVIEW_HEIGHT_PX,
        ),
      );

  return (
    <div
      ref={tableWrapRef}
      className={`ud1000ReportScaleWrap${
        disableResponsiveScale ? " is-fullSize" : ""
      }`}
      style={{
        "--ud1000-report-scale": responsiveLayout.scale,
        "--ud1000-report-table-width": `${TARGET_TABLE_WIDTH_PX}px`,
        "--ud1000-report-max-viewport-height":
          maxViewportHeight == null ? "none" : `${maxViewportHeight}px`,
        "--ud1000-report-min-height": `${minPreviewHeight}px`,
        "--ud1000-report-scaled-height":
          responsiveLayout.height == null
            ? "auto"
            : `${responsiveLayout.height}px`,
      }}
    >
      <div className="ud1000ReportScaleStage">
        <div ref={tableStageRef} className="ud1000ReportScaleContent">
          <table
            ref={tableRef}
            className="ud1000Report typeVideo"
            spellCheck={false}
            style={{ width: `${TARGET_TABLE_WIDTH_PX}px` }}
          >
            <caption>{t("videoFileIndex.report.title")}</caption>
            <tbody>
              <tr>
                <th>{t("videoFileIndex.report.facilityType")}</th>
                <td>{draftData?.facility_type}</td>
                <th>{t("videoFileIndex.report.facilityName")}</th>
                <td colSpan={3}>{draftData?.facility_name}</td>
                <th>{t("videoFileIndex.report.partName")}</th>
                <td>{draftData?.part_name}</td>
              </tr>

              <tr>
                <th>{t("videoFileIndex.report.buildingName")}</th>
                <td>{draftData?.building_name}</td>
                <th>{t("videoFileIndex.report.floor")}</th>
                <td>{draftData?.floor}</td>
                <th>{t("videoFileIndex.report.location")}</th>
                <td>{draftData?.location}</td>
                <th>{t("videoFileIndex.report.shootingDate")}</th>
                <td>{draftData?.shooting_date}</td>
              </tr>

              <tr>
                <th>
                  {t("videoFileIndex.report.ultrasonicIntensity")}
                  <br />
                  <span>(dB)</span>
                </th>
                <td className="readOnly" id="avg_decibel">
                  A: {draftData?.frame_decibel_1 ?? "--"} / B:{" "}
                  {draftData?.frame_decibel_2 ?? "--"}
                </td>

                <th>
                  {t("videoFileIndex.report.thermalTemperature")}
                  <br />
                  <span>(°C)</span>
                </th>
                <td className="readOnly">
                  <div className="thermalValue">
                    {t("videoFileIndex.report.thermal.max")}:{" "}
                    {draftData?.thermal_max}
                    <br />
                    {t("videoFileIndex.report.thermal.min")}:{" "}
                    {draftData?.thermal_min}
                    <br />
                    {t("videoFileIndex.report.thermal.avg")}:{" "}
                    {draftData?.thermal_avg}
                  </div>
                </td>

                <th>{t("videoFileIndex.report.uvDetected")}</th>
                <td className="readOnly">-</td>
                <th style={{ whiteSpace: "pre-line" }}>
                  {t("videoFileIndex.report.fusionResultMultiline")}
                </th>
                <td>{toFusionLabel(draftData?.fusion_result)}</td>
              </tr>

              <tr>
                <th>
                  {t("videoFileIndex.report.measurementDistance")}
                  <br />
                  <span>(m)</span>
                </th>
                <td className="readOnly">
                  A: {draftData?.frame_distance_1 ?? "--"} / B:{" "}
                  {draftData?.frame_distance_2 ?? "--"}
                </td>

                <th>
                  {t("videoFileIndex.report.ambientTemperature")}
                  <br />
                  <span>(°C)</span>
                </th>
                <td>{draftData?.ambient_temp}</td>

                <th>{t("videoFileIndex.report.equipmentUsed")}</th>
                <td>{draftData?.equipment_used}</td>

                <th>{t("videoFileIndex.report.serialNumber")}</th>
                <td>{draftData?.serial_number}</td>
              </tr>

              <tr>
                <th rowSpan={2}>
                  {t("videoFileIndex.report.inspectionResult.title")}
                  <br />
                  <span>
                    {t("videoFileIndex.report.inspectionResult.label")}
                  </span>
                </th>
                <td className="fftLabelRow" rowSpan={2} colSpan={7}>
                  <div>{draftData?.inspection_result}</div>
                </td>
              </tr>
              <tr></tr>

              <tr className="fftLabelRow">
                <th colSpan={4}>
                  {t("videoFileIndex.report.ultrasonicDiagnosisWithTime", {
                    label: "A",
                    time: draftData?.time_1 ?? "",
                  })}
                </th>
                <th colSpan={4}>
                  {t("videoFileIndex.report.fastFourierTransformWithLabel", {
                    label: "A",
                  })}
                </th>
              </tr>

              <tr>
                <td rowSpan={3} colSpan={4} className="imgCell">
                  <img
                    ref={ultrasonicRef1}
                    src={getSafeImageSrc(imgTemp?.frame_image_1)}
                    alt="ultrasonic shooting (A)"
                  />
                </td>
                <td colSpan={4} className="imgCell">
                  <div className="table_graphBox" ref={fftRef1}>
                    <img
                      src={getSafeImageSrc(imgTemp?.graph_image_1)}
                      alt="fftgraph (A)"
                    />
                  </div>
                </td>
              </tr>
              <tr style={{ height: "46px", fontSize: "18px" }}>
                <th colSpan={4}>
                  {t("videoFileIndex.report.waveformWithLabel", { label: "A" })}
                </th>
              </tr>
              <tr>
                <td colSpan={4} className="imgCell">
                  <img
                    src={getSafeImageSrc(imgTemp?.waveform_image_1)}
                    alt="waveform (A)"
                  />
                </td>
              </tr>
              <tr>
                <th colSpan={4}>
                  {t("videoFileIndex.report.ultrasonicDiagnosisWithTime", {
                    label: "B",
                    time: draftData?.time_2 ?? "",
                  })}
                </th>
                <th colSpan={4}>
                  {t("videoFileIndex.report.fastFourierTransformWithLabel", {
                    label: "B",
                  })}
                </th>
              </tr>
              <tr>
                <td className="imgCell" rowSpan={3} colSpan={4}>
                  <img
                    ref={ultrasonicRef2}
                    src={getSafeImageSrc(imgTemp?.frame_image_2)}
                    alt="ultrasonic shooting (B)"
                  />
                </td>
                <td className="imgCell" colSpan={4}>
                  <div className="table_graphBox" ref={fftRef2}>
                    <img
                      src={getSafeImageSrc(imgTemp?.graph_image_2)}
                      alt="fftgraph (B)"
                    />
                  </div>
                </td>
              </tr>
              <tr style={{ height: "46px", fontSize: "18px" }}>
                <th colSpan={4}>
                  {t("videoFileIndex.report.waveformWithLabel", { label: "B" })}
                </th>
              </tr>
              <tr>
                <td colSpan={4} className="imgCell">
                  <img
                    src={getSafeImageSrc(imgTemp?.waveform_image_2)}
                    alt="waveform (B)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
